import { Component, Input } from '@angular/core';
import { ListItemComponent, ListViewComponent } from 'components/layout/list-view/list-view.component';
import { DomainAction } from "models/generated/Distribution.Api.Models.AddDomainRequest";
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { DnsInformationModel, DomainService } from 'services/domain.service';
import { NavigationService } from 'services/navigation.service';
import { DomainDnsDetailsComponent } from './domain-dns-details.component';
import { LookupDomainComponent } from './lookup-domain.component';
import { DomainAvailabilityStatus } from 'enums/DomainAvailabilityStatus';
import { SelectListItemModel } from 'models/SelectList';
import { Inputs } from 'modules/inputs';
import { FormValidationService } from 'services/form-validation.service';
import { Subscription } from 'rxjs';

@Component({
	selector: 'add-domain',
	templateUrl: './add-domain.component.html',
	providers: [CompanyDataProviderFactory, FormValidationService],
	imports: [Layout, Inputs, LookupDomainComponent, ListViewComponent, ListItemComponent, DomainDnsDetailsComponent, TranslatePipe]
})
export class AddDomainComponent {

	private dataProvider?: CompanyDataProvider;
	private _companyId?: string;
	private _nameServerResponse?: DnsInformationModel;
	private checkingNameConflict?: Subscription;
	@Input()
	set companyId(id: string | undefined) {
		this._companyId = id;
		if (id) this.dataProvider = this.dataProviderFactory.getProviderForCompanyId(id);
	}
	get companyId() {
		return this._companyId;
	}
	@Input() productPlan?: ProductPlanModel;
	@Input() registerNewDomain = false;
	lookupBusy = false;
	sameCompanyAlreadyHasDomain = false;
	anyCompanyAlreadyHasDomain?: boolean;
	domainIsAvailable?: boolean;
	domainAction?: DomainAction.Nothing | DomainAction.AddDomainForDns;
	dnsOptions: SelectListItemModel[];

	set nameServerResponse(response) {
		this.domainIsAvailable = undefined;
		this._nameServerResponse = response;
		const domainName = this._nameServerResponse?.name;

		this.dataProvider?.getDomains().then(domains => {
			this.sameCompanyAlreadyHasDomain = domains.some(domain => domain.name === domainName);
			if (domainName) {
				this.domainService.getDomainAvailabilityStatus(domainName).then(status => { this.domainIsAvailable = (status === DomainAvailabilityStatus.Available); });
			}
		});

		this.checkingNameConflict?.unsubscribe();
		this.anyCompanyAlreadyHasDomain = undefined;
		if (domainName) this.checkingNameConflict = this.domainService.getDomainsByName(domainName).subscribe(conflictingDomains => {
			this.anyCompanyAlreadyHasDomain = conflictingDomains.length > 0;
		});
	}
	get nameServerResponse() { return this._nameServerResponse; }

	get domainName() { return this.nameServerResponse?.name; }

	constructor(
		private readonly navigation: NavigationService,
		private readonly dataProviderFactory: CompanyDataProviderFactory,
		private readonly domainService: DomainService,
		translate: TranslatePipe) {

		this.dnsOptions = [new SelectListItemModel(DomainAction.AddDomainForDns, translate.transform('company.domains.add-for-dns')), new SelectListItemModel(DomainAction.Nothing, translate.transform('company.domains.add-without-actions'))];
	}

	async addDomain() {
		if (!this.companyId || !this.domainName || this.domainAction === undefined) return;

		const registrantInfo = await this.getRegistrantInfo();
		if (!registrantInfo) return;

		await this.domainService.addDomainToCompany(this.domainName, registrantInfo, this.companyId, this.domainAction);
		this.navigation.closeView(this);
	}

	async buyDomain() {
		if (!this.companyId || !this.domainName || !this.productPlan?.id) return;

		const registrantInfo = await this.getRegistrantInfo();
		if (!registrantInfo) return;

		await this.domainService.buyDomainForProductPlan(this.domainName, registrantInfo, this.productPlan.id, this.companyId);
		this.navigation.closeView(this);
	}

	private async getRegistrantInfo() {
		if (!this.companyId || !this.dataProvider) return;
		return this.domainService.getRegistrantInfo(await this.dataProvider.getCompany());
	}

	loading(): boolean {
		return this.lookupBusy || (this.nameServerResponse !== undefined && this.domainIsAvailable === undefined)
	}
}
